import { z } from 'zod';
import { lenientString, lenientUuid, lenientDateString, lenientRequiredNode } from '../schema-extensions';
import { ItemType } from '../enums/item-type.enum';
import { ValueHistoryNode } from './value-history-node-schema';

// ============================================================================
// CONSTANTS (must be defined before schemas that reference them)
// ============================================================================
const ITEM_CONSTANTS = {
  MAKE_MAX_LENGTH: 50,
  MODEL_MAX_LENGTH: 50,
  SERIAL_MAX_LENGTH: 100,
  WARRANTY_NUMBER_MAX_LENGTH: 50,
  DATE_FORMAT_LENGTH: 10, // 'YYYY-MM-DD'
  NOTES_MAX_LENGTH: 1000,
};

// ============================================================================
// SCHEMAS
// ============================================================================

/**
 * Lenient schema for item - accepts various formats and coerces to standard
 */
const lenientItemNodeSchema = z.object({
  id: lenientUuid(),
  type: z.nativeEnum(ItemType),
  make: lenientString(ITEM_CONSTANTS.MAKE_MAX_LENGTH),
  model: lenientString(ITEM_CONSTANTS.MODEL_MAX_LENGTH),
  serial: lenientString(ITEM_CONSTANTS.SERIAL_MAX_LENGTH),
  ownerId: z.string().optional(),
  warrantyNumber: lenientString(ITEM_CONSTANTS.WARRANTY_NUMBER_MAX_LENGTH),
  warrantyExpiryDate: lenientDateString(''),
  value: lenientRequiredNode(ValueHistoryNode),
  notes: lenientString(ITEM_CONSTANTS.NOTES_MAX_LENGTH),
}).transform(val => ({
  id: val.id,
  type: val.type,
  make: val.make,
  model: val.model,
  serial: val.serial,
  ownerId: val.ownerId,
  warrantyNumber: val.warrantyNumber,
  warrantyExpiryDate: val.warrantyExpiryDate,
  value: val.value,
  notes: val.notes
}));

/**
 * Strict schema for item - enforces exact format
 */
const strictItemNodeSchema = z.object({
  id: z.string().min(1),
  type: z.nativeEnum(ItemType),
  make: z.string().max(ITEM_CONSTANTS.MAKE_MAX_LENGTH),
  model: z.string().max(ITEM_CONSTANTS.MODEL_MAX_LENGTH),
  serial: z.string().max(ITEM_CONSTANTS.SERIAL_MAX_LENGTH),
  ownerId: z.string().optional(),
  warrantyNumber: z.string().max(ITEM_CONSTANTS.WARRANTY_NUMBER_MAX_LENGTH),
  warrantyExpiryDate: z.string().length(ITEM_CONSTANTS.DATE_FORMAT_LENGTH),
  value: z.unknown().transform(val => ValueHistoryNode.deserialize(val)),
  notes: z.string().max(ITEM_CONSTANTS.NOTES_MAX_LENGTH),
}).transform(val => ({
  id: val.id,
  type: val.type,
  make: val.make,
  model: val.model,
  serial: val.serial,
  ownerId: val.ownerId,
  warrantyNumber: val.warrantyNumber,
  warrantyExpiryDate: val.warrantyExpiryDate,
  value: val.value,
  notes: val.notes
}));

// ============================================================================
// INTERFACE
// ============================================================================

export interface ItemNode {
  id: string;
  type: ItemType;
  make: string;
  model: string;
  serial: string;
  ownerId?: string;
  warrantyNumber: string;
  warrantyExpiryDate: string;
  value: ValueHistoryNode;
  notes: string;
}

// ============================================================================
// NAMESPACE WITH METHODS
// ============================================================================

export namespace ItemNode {
  /**
   * Safely deserialize unknown data into ItemNode
   * Uses lenient parsing to handle various input formats
   */
  export function deserialize(data: unknown): ItemNode {
    return lenientItemNodeSchema.parse(data);
  }

  /**
   * Create a new ItemNode with validation
   * Uses strict parsing to ensure data integrity
   */
  export function create(data: ItemNode): ItemNode {
    return strictItemNodeSchema.parse(data);
  }

  /**
   * Validate that data conforms to ItemNode structure
   */
  export function validate(data: unknown): data is ItemNode {
    return strictItemNodeSchema.safeParse(data).success;
  }

  /**
   * Get a display name for the item
   */
  export function getDisplayName(item: ItemNode): string {
    return `${item.make} ${item.model}`;
  }

  /**
   * Get the current value of the item
   */
  export function getCurrentValue(item: ItemNode): number | undefined {
    return ValueHistoryNode.getCurrentValue(item.value);
  }

  /**
   * Check if the item has been sold
   */
  export function isSold(item: ItemNode): boolean {
    return ValueHistoryNode.isSold(item.value);
  }

  export function isActive(item: ItemNode): boolean {
    return !ItemNode.isSold(item);
  }

  /**
   * Check if warranty is still valid
   */
  export function isWarrantyValid(item: ItemNode): boolean {
    const today = new Date().toISOString().slice(0, 10);
    return item.warrantyExpiryDate >= today;
  }

  /**
   * Constants for validation and UI
   */
  export const Constants = ITEM_CONSTANTS;
}
